/**
 * @module app.cm
 */

import { autoinject } from 'aurelia-framework';
import { ComponentBase } from 'app/shared/lib/essette-site';
import { ITasksConfig } from 'app/core/models/essette-interfaces';
import { ITaskContainer, ISmartTaskBinding } from 'app/core/components/tasks/task';
import { CaseService } from 'app/cm/services/case-service';
import { EssForm } from 'app/shared/elements/forms/ess-form';
import { validate } from 'app/shared/elements/ess-rule';
import { apiCall } from 'app/shared/lib/api/api';
import { openCaseSummary } from 'app/cm/components/summary/case-summary'; //which exports that function
// 'app/cm/pages/manage-cases/create-case' exports nothing but, we have an anchor tag for that.
import * as CM from 'app/cm/models/essette-api';
import * as Core from 'app/core/models/essette-api';


@autoinject()
export class SmartCase extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    protected cases: ReadonlyArray<CM.Case> = [];

    protected memberId!: string;
    protected originCode!: string;
    private caseId: string | null = null;

    protected task!: Core.Task;
    protected targetId!: string;
    protected tasksConfig: ITasksConfig = {};
    protected readonly: boolean = false;

    private container!: ITaskContainer;
    protected $isValid!: boolean;
    protected validate = validate; //raise to UI

    protected hasCreateCaseRoute: boolean = false;
    protected hasCaseSummaryRoute: boolean = false;
    protected hasCaseRoute = false;
    protected openCaseSummary = openCaseSummary;
    protected shouldRedirect: boolean = false;

    ////****************** Element References ***************************************////
    public $form!: EssForm;


    ////****************** Lifecycle events (in order of execution) *****************////

    constructor(
        private readonly service: CaseService,
        ...rest: any[]) {

        super(...rest);
        this.logger.source = this;
    }

    protected async activate(params: ISmartTaskBinding): Promise<void> {
        if(!params || !params.container || !params.targetId || !params.task || !params.task.id || !params.tasksConfig || !params.tasksConfig.smartTasksConfig || !params.tasksConfig.smartTasksConfig.notesConfig) {
            throw TypeError(`${SmartCase.name} Component Model not Bound.`);
        }

        this.targetId = params.targetId;
        this.tasksConfig = params.tasksConfig;
        this.memberId = params.relatedModels?.['CD.Member'] || ''; //might also take it from cases[0]
        this.originCode = params.tasksConfig.smartTasksConfig?.moduleTaskCode || '';

        this.state = params.state || this.state;

        this.task = params.task;
        this.container = params.container;
        this.readonly = params.readonly;
    }

    protected async bind(): Promise<void> {
        this.loadCases();
        this.checkPermissions(); //has to be here instead of usual activate() so the UI can get it
    }


    ////****************** Events ***************************************************////
    protected async referCaseSuccess(): Promise<void> {
        if(this.container) {
            await this.container.completeTask(true);

            if(this.shouldRedirect && this.caseId) {
                this.router.navigateToRoute('cm.case', { caseId: this.caseId });
            } else {
                this.task.isCompleted = true;
                this.loadCases();
            }
        }
    }

    protected async submit(redirect: boolean = false): Promise<boolean> {
        this.shouldRedirect = redirect;
        return this.$form.submit();
    }

    ////****************** Functions ************************************************////
    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.hasCreateCaseRoute = this.accountContext.hasRouteAccess('cm.manage-cases.create');
        this.hasCaseRoute = this.accountContext.hasRouteAccess('cm.case');
        this.hasCaseSummaryRoute = this.accountContext.hasRouteAccess('cm.case.summary');
    }


    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadCases(): Promise<void> {
        if(!this.task.id) { return; }

        try {
            this.state.beginLoad();

            const includes = ['referral', 'referral.source', 'referral.destination', 'referral.types', 'referral.reason'];
            const response = await this.service.loadCasesCreatedByTask(this.task.id, { all: true }, includes);
            this.cases = response.results;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

}
